/**
 * Dashboard App Logic - Sequence Management
 */

// Initialize Sentry as early as possible
import * as Sentry from "@sentry/browser";

Sentry.init({
  dsn: "https://98f444c73599f68edcf65d9db57c665f@o4510773732245504.ingest.us.sentry.io/4510774427189248",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
  ],
  _experiments: {
    enableLogs: true,
  },
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.xrgallery\.io/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

import { requireAuth, getCurrentUser, logOut, onAuthChange } from '../src/firebase/auth';
import {
  createSequence,
  updateSequence,
  listUserSequences,
  listOrgSequences,
  deleteSequence,
  toggleSequenceVisibility,
  duplicateSequence,
  getShareableLink,
  getEmbedCode,
  type Sequence
} from '../src/api/SequenceAPI';
import {
  createOrganization,
  listUserOrganizations,
  inviteMember,
  getPendingInvitations,
  acceptInvitation,
  declineInvitation,
  type Organization
} from '../src/api/OrganizationAPI';
import {
  ensureUserProfile,
  getUserProfile,
  toggleUserAnalytics,
  hasFeatureAccess,
  type UserProfile,
  type FeatureKey
} from '../src/api/UserProfileAPI';
import { downloadTourPackage, type ExportOptions } from '../src/export/TourExporter';
import type { ExperienceConfig } from '../src/ui/UIManager';
import * as bcrypt from 'bcryptjs';
import { showUpgradeModal } from '../src/ui/UpgradeModal';
import { showAlert, showConfirm } from '../src/ui/ModalManager';
import { isFeatureEnabled } from '../src/config/featureFlags';
import { showUsageModal, showSettingsModal, setupDropdownToggle, triggerToolbarAnimations, setBadgeTier } from '../src/ui/UserDropdown';
import { Logger } from '../src/utils/Logger';
import { initWebVitals } from '../src/utils/webVitals';
import { analytics } from '../src/firebase/config';
import type { User } from 'firebase/auth';

const logger = Logger.getLogger('Dashboard');

// Initialize Web Vitals tracking (sends Core Web Vitals to Firebase Analytics)
initWebVitals(analytics);

/**
 * Show toast notification
 */
function showToast(message: string, type: 'success' | 'error' | 'info' = 'success', duration: number = 3000) {
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;

  const icon = type === 'success' ? '✓' : type === 'error' ? '✗' : 'ℹ';

  toast.innerHTML = `
    <span class="toast-icon">${icon}</span>
    <span>${message}</span>
  `;

  document.body.appendChild(toast);

  // Trigger animation
  setTimeout(() => toast.classList.add('show'), 10);

  // Auto-remove
  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

// DOM Elements
let userEmailSpan: HTMLElement;
let logoutBtn: HTMLButtonElement;
let upgradeBtn: HTMLButtonElement | null;
let proBadge: HTMLElement | null;
let adminDashboardBtn: HTMLElement | null;
let createSequenceBtn: HTMLButtonElement;
let sequencesGrid: HTMLElement;
let emptyState: HTMLElement;

let workspaceSelector: HTMLSelectElement | null;
let createOrgBtn: HTMLButtonElement | null;
let inviteMemberBtn: HTMLButtonElement | null;

let sequenceModal: HTMLElement;
let sequenceForm: HTMLFormElement;
let modalTitle: HTMLElement;
let cancelBtn: HTMLButtonElement;
let saveBtn: HTMLButtonElement;
let saveBtnText: HTMLElement;

let sequenceNameInput: HTMLInputElement;
let sequenceDescInput: HTMLTextAreaElement;
let sequencePublicCheckbox: HTMLInputElement;
let discoverableGroup: HTMLElement;
let sequenceDiscoverableCheckbox: HTMLInputElement;
let sequencePasswordInput: HTMLInputElement;
let passwordStatus: HTMLElement;
let passwordStatusText: HTMLElement;
let passwordHelpText: HTMLElement;
let removePasswordBtn: HTMLButtonElement;
let sequenceAnalyticsCheckbox: HTMLInputElement;

let orgModal: HTMLElement;
let orgForm: HTMLFormElement;
let cancelOrgBtn: HTMLButtonElement;
let saveOrgBtn: HTMLButtonElement;
let orgNameInput: HTMLInputElement;

let inviteModal: HTMLElement | null;
let inviteForm: HTMLFormElement | null;
let cancelInviteBtn: HTMLButtonElement | null;
let sendInviteBtn: HTMLButtonElement;
let inviteEmailInput: HTMLInputElement;

let embedModal: HTMLElement;
let embedWidthInput: HTMLInputElement;
let embedHeightInput: HTMLInputElement;
let embedControlsCheckbox: HTMLInputElement;
let embedBrandingCheckbox: HTMLInputElement;
let embedAutoplayCheckbox: HTMLInputElement;
let embedCodeTextarea: HTMLTextAreaElement;
let closeEmbedBtn: HTMLButtonElement;
let copyEmbedBtn: HTMLButtonElement;

// Export Modal Elements
let exportModal: HTMLElement | null;
let exportBrandingCheckbox: HTMLInputElement | null;
let exportControlsCheckbox: HTMLInputElement | null;
let closeExportBtn: HTMLButtonElement | null;
let downloadExportBtn: HTMLButtonElement | null;

let userSettingsBtn: HTMLButtonElement;
let userSettingsModal: HTMLElement;
let userSettingsForm: HTMLFormElement;
let cancelUserSettingsBtn: HTMLButtonElement;
let userAnalyticsCheckbox: HTMLInputElement | null;

let userDropdown: HTMLElement;
let userDropdownToggle: HTMLElement;

function initializeDOM() {
  userEmailSpan = document.getElementById('user-email') as HTMLElement;
  logoutBtn = document.getElementById('user-signout-btn') as HTMLButtonElement;
  upgradeBtn = document.getElementById('upgrade-btn') as HTMLButtonElement | null;
  proBadge = document.getElementById('pro-badge') as HTMLElement | null;
  adminDashboardBtn = document.getElementById('admin-dashboard-btn') as HTMLElement | null;
  createSequenceBtn = document.getElementById('create-sequence-btn') as HTMLButtonElement;
  sequencesGrid = document.getElementById('sequences-grid') as HTMLElement;
  emptyState = document.getElementById('empty-state') as HTMLElement;

  workspaceSelector = document.getElementById('workspace-selector') as HTMLSelectElement | null;
  createOrgBtn = document.getElementById('create-org-btn') as HTMLButtonElement | null;
  inviteMemberBtn = document.getElementById('invite-member-btn') as HTMLButtonElement | null;

  sequenceModal = document.getElementById('sequence-modal') as HTMLElement;
  sequenceForm = document.getElementById('sequence-form') as HTMLFormElement;
  modalTitle = document.getElementById('modal-title') as HTMLElement;
  cancelBtn = document.getElementById('cancel-btn') as HTMLButtonElement;
  saveBtn = document.getElementById('save-btn') as HTMLButtonElement;
  saveBtnText = document.getElementById('save-btn-text') as HTMLElement;

  sequenceNameInput = document.getElementById('sequence-name') as HTMLInputElement;
  sequenceDescInput = document.getElementById('sequence-description') as HTMLTextAreaElement;
  sequencePublicCheckbox = document.getElementById('sequence-public') as HTMLInputElement;
  discoverableGroup = document.getElementById('discoverable-group') as HTMLElement;
  sequenceDiscoverableCheckbox = document.getElementById('sequence-discoverable') as HTMLInputElement;
  sequencePasswordInput = document.getElementById('sequence-password') as HTMLInputElement;
  passwordStatus = document.getElementById('password-status') as HTMLElement;
  passwordStatusText = document.getElementById('password-status-text') as HTMLElement;
  passwordHelpText = document.getElementById('password-help-text') as HTMLElement;
  removePasswordBtn = document.getElementById('remove-password-btn') as HTMLButtonElement;
  sequenceAnalyticsCheckbox = document.getElementById('sequence-analytics') as HTMLInputElement;

  orgModal = document.getElementById('org-modal') as HTMLElement;
  orgForm = document.getElementById('org-form') as HTMLFormElement;
  cancelOrgBtn = document.getElementById('cancel-org-btn') as HTMLButtonElement;
  saveOrgBtn = document.getElementById('save-org-btn') as HTMLButtonElement;
  orgNameInput = document.getElementById('org-name') as HTMLInputElement;

  inviteModal = document.getElementById('invite-modal') as HTMLElement | null;
  inviteForm = document.getElementById('invite-form') as HTMLFormElement | null;
  cancelInviteBtn = document.getElementById('cancel-invite-btn') as HTMLButtonElement | null;
  sendInviteBtn = document.getElementById('send-invite-btn') as HTMLButtonElement;
  inviteEmailInput = document.getElementById('invite-email') as HTMLInputElement;

  embedModal = document.getElementById('embed-modal') as HTMLElement;
  embedWidthInput = document.getElementById('embed-width') as HTMLInputElement;
  embedHeightInput = document.getElementById('embed-height') as HTMLInputElement;
  embedControlsCheckbox = document.getElementById('embed-controls') as HTMLInputElement;
  embedBrandingCheckbox = document.getElementById('embed-branding') as HTMLInputElement;
  embedAutoplayCheckbox = document.getElementById('embed-autoplay') as HTMLInputElement;
  embedCodeTextarea = document.getElementById('embed-code') as HTMLTextAreaElement;
  closeEmbedBtn = document.getElementById('close-embed-btn') as HTMLButtonElement;
  copyEmbedBtn = document.getElementById('copy-embed-btn') as HTMLButtonElement;

  // Export Modal Elements
  exportModal = document.getElementById('export-modal') as HTMLElement | null;
  exportBrandingCheckbox = document.getElementById('export-branding') as HTMLInputElement | null;
  exportControlsCheckbox = document.getElementById('export-controls') as HTMLInputElement | null;
  closeExportBtn = document.getElementById('close-export-btn') as HTMLButtonElement | null;
  downloadExportBtn = document.getElementById('download-export-btn') as HTMLButtonElement | null;

  userSettingsBtn = document.getElementById('user-settings-btn') as HTMLButtonElement;
  userSettingsModal = document.getElementById('user-settings-modal') as HTMLElement;
  userSettingsForm = document.getElementById('user-settings-form') as HTMLFormElement;
  cancelUserSettingsBtn = document.getElementById('cancel-user-settings-btn') as HTMLButtonElement;
  userAnalyticsCheckbox = document.getElementById('user-analytics-enabled') as HTMLInputElement | null;

  userDropdown = document.getElementById('user-dropdown') as HTMLElement;
  userDropdownToggle = document.getElementById('user-dropdown-toggle') as HTMLElement;
  
  logger.debug('DOM Initialized. Settings Btn:', !!userSettingsBtn, 'Settings Modal:', !!userSettingsModal);
}

// State
let currentUser: User | null = null;
let currentUserProfile: UserProfile | null = null;
let sequences: Sequence[] = [];
let organizations: Organization[] = [];
let currentWorkspace: 'personal' | string = 'personal'; // 'personal' or orgId
let editingSequenceId: string | null = null;

/**
 * Handle Stripe redirect parameters (after checkout success/cancel)
 */
function handleStripeRedirects() {
  const urlParams = new URLSearchParams(window.location.search);

  // Check if user just upgraded successfully
  if (urlParams.get('upgraded') === 'true') {
    const tier = urlParams.get('tier');
    const tierName = tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : 'Pro';

    // Clear URL params immediately to prevent re-showing on refresh
    window.history.replaceState({}, document.title, '/dashboard/');

    // Show success message after a short delay to ensure DOM is ready
    setTimeout(() => {
      showAlert(
        `🎉 Welcome to ${tierName}!\n\nYour upgrade was successful. All premium features are now unlocked.`,
        'Upgrade Complete',
        '🎊'
      );
    }, 500);

    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('tier-updated', { detail: { tier: tier || 'pro' } }));
  }

  // Check if user canceled the checkout
  if (urlParams.get('canceled') === 'true') {
    // Clear URL params
    window.history.replaceState({}, document.title, '/dashboard/');

    setTimeout(() => {
      showAlert(
        'No worries! You can upgrade anytime from your dashboard.',
        'Checkout Canceled',
        'ℹ️'
      );
    }, 500);
  }
}

// triggerToolbarAnimations moved to shared UserDropdown.ts

/**
 * Initialize dashboard
 */
async function init() {
  try {
    // Handle Stripe redirect parameters
    handleStripeRedirects();

    // Require authentication
    currentUser = requireAuth();

    // Show user email
    userEmailSpan.textContent = currentUser.email || 'User';
    const mobileUserEmailSpan = document.getElementById('mobile-user-email');
    if (mobileUserEmailSpan) {
      mobileUserEmailSpan.textContent = currentUser.email || 'User';
    }

    // Ensure user profile exists (create if first time)
    currentUserProfile = await ensureUserProfile(currentUser.uid, currentUser.email, currentUser.displayName);
    logger.info('User profile initialized');

    // Check tier and update UI
    updateTierUI();

    // Trigger toolbar animations NOW - we have user profile/tier info
    // Don't wait for sequences/organizations to load
    triggerToolbarAnimations('header-toolbar-animate');

    // Load organizations
    await loadOrganizations();

    // Load sequences
    await loadSequences();

    logger.info('Dashboard initialized');
  } catch (error) {
    logger.error('Failed to initialize dashboard:', error);
    // Still trigger animations on error so UI isn't stuck invisible
    triggerToolbarAnimations('header-toolbar-animate');
  }
}

/**
 * Update UI based on user tier
 */
function updateTierUI() {
  if (!currentUserProfile) return;

  const hasPro = hasFeatureAccess(currentUserProfile, 'uploads'); // Pro or Admin

  // Lock create organization button for free users (if button exists)
  if (!hasPro && createOrgBtn) {
    createOrgBtn.textContent = '+ New Organization (Pro)';
    createOrgBtn.classList.add('locked');
    createOrgBtn.title = 'Upgrade to Pro to create organizations';
  }
}

/**
 * Load user organizations and populate workspace selector
 */
async function loadOrganizations() {
  try {
    organizations = await listUserOrganizations(currentUser.uid);

    // Populate workspace selector (if present in UI)
    if (workspaceSelector) {
      // Clear existing options safely
      workspaceSelector.replaceChildren();

      // Add personal workspace option
      const personalOption = document.createElement('option');
      personalOption.value = 'personal';
      personalOption.textContent = 'My Personal Workspace';
      workspaceSelector.appendChild(personalOption);

      // Add organization options
      organizations.forEach((org) => {
        const option = document.createElement('option');
        option.value = org.id!;
        option.textContent = org.name;
        workspaceSelector.appendChild(option);
      });
    }

    logger.debug(`Loaded ${organizations.length} organizations`);

    // Update Pro badge after organizations are loaded
    updateProBadge();
  } catch (error) {
    logger.error('Failed to load organizations:', error);
  }
}

// Note: setBadgeTier moved to shared UserDropdown.ts (imported at top)

/**
 * Update tier badge visibility based on current workspace and user tier
 * Note: This is custom for dashboard because it has workspace-aware logic
 */
function updateProBadge() {
  if (!upgradeBtn || !proBadge) return;

  const userTier = currentUserProfile?.tier ?? 'free';

  // Show/hide admin dashboard button based on user tier
  if (adminDashboardBtn) {
    adminDashboardBtn.style.display = userTier === 'admin' ? 'flex' : 'none';
  }

  // Show/hide newsletter admin button based on user tier
  const newsletterAdminBtn = document.getElementById('newsletter-admin-btn');
  if (newsletterAdminBtn) {
    newsletterAdminBtn.style.display = userTier === 'admin' ? 'flex' : 'none';
  }

  // Show/hide blog admin button based on user tier
  const blogAdminBtn = document.getElementById('blog-admin-btn');
  if (blogAdminBtn) {
    blogAdminBtn.style.display = userTier === 'admin' ? 'flex' : 'none';
  }

  // Show/hide admin items in mobile menu
  document.querySelectorAll('.admin-mobile-btn').forEach((el) => {
    (el as HTMLElement).style.display = userTier === 'admin' ? 'flex' : 'none';
  });

  // Show/hide admin items in dropdown menu
  document.querySelectorAll('.admin-dropdown-btn').forEach((el) => {
    (el as HTMLElement).style.display = userTier === 'admin' ? 'flex' : 'none';
  });

  // Check if current workspace is an organization with a paid plan
  if (currentWorkspace !== 'personal') {
    const currentOrg = organizations.find(org => org.id === currentWorkspace);
    if (currentOrg && (currentOrg.plan === 'pro' || currentOrg.plan === 'enterprise')) {
      // Hide upgrade button, show tier badge
      upgradeBtn.style.display = 'none';
      setBadgeTier(proBadge, currentOrg.plan, currentOrg.plan === 'enterprise' ? 'ENTERPRISE' : 'PRO');
      return;
    }
  }

  // Personal workspace - show badge based on user tier
  if (userTier === 'admin') {
    upgradeBtn.style.display = 'none';
    setBadgeTier(proBadge, 'admin', 'ADMIN');
    return;
  }

  if (userTier === 'enterprise') {
    upgradeBtn.style.display = 'none';
    setBadgeTier(proBadge, 'enterprise', 'ENTERPRISE');
    return;
  }

  if (userTier === 'pro') {
    upgradeBtn.style.display = 'none';
    setBadgeTier(proBadge, 'pro', 'PRO');
    return;
  }

  if (userTier === 'basic') {
    // Show "BASIC" badge but also show upgrade button since they can upgrade
    upgradeBtn.style.display = 'inline-block';
    setBadgeTier(proBadge, 'basic', 'BASIC');
    return;
  }

  // Free tier: show upgrade button, hide badge
  upgradeBtn.style.display = 'inline-block';
  proBadge.style.display = 'none';
}

/**
 * Load user sequences based on current workspace
 */
async function loadSequences() {
  try {
    if (currentWorkspace === 'personal') {
      // Load personal sequences (userId matches and orgId is null)
      sequences = await listUserSequences(currentUser.uid);
    } else {
      // Load organization sequences
      sequences = await listOrgSequences(currentWorkspace);
    }

    if (sequences.length === 0) {
      // Show empty state
      sequencesGrid.style.display = 'none';
      emptyState.style.display = 'block';
      // Show welcome banner for new users
      const welcomeBanner = document.getElementById('welcome-banner');
      if (welcomeBanner && !localStorage.getItem('xrg-welcome-dismissed')) {
        welcomeBanner.style.display = 'block';
      }
    } else {
      // Show sequences
      sequencesGrid.style.display = 'grid';
      emptyState.style.display = 'none';
      renderSequences();
    }
  } catch (error) {
    logger.error('Failed to load sequences:', error);
    showAlert('Failed to load sequences. Please refresh the page.', 'Error', '❌');
  }
}

/**
 * Check if user can export tours (available to all tiers with self_host_export feature + feature flag)
 */
function canExportTours(): boolean {
  if (!isFeatureEnabled('SELF_HOST_EXPORT')) return false;
  return currentUserProfile ? hasFeatureAccess(currentUserProfile, 'self_host_export') : false;
}

/**
 * Render sequences grid
 */
function renderSequences() {
  const canExport = canExportTours();

  sequencesGrid.innerHTML = sequences
    .map((seq) => {
      const createdDate = seq.createdAt.toDate().toLocaleDateString();
      const stageCount = seq.config.stages?.length || 0;

      return `
        <div class="sequence-card" data-id="${seq.id}">
          <div class="sequence-header">
            <div>
              <h3 class="sequence-name">${escapeHtml(seq.name)}</h3>
              <div class="sequence-badges">
                <span class="visibility-badge ${seq.isPublic ? 'public' : 'private'}">
                  ${seq.isPublic ? 'Public' : 'Private'}
                </span>
                ${seq.password ? '<span class="visibility-badge" style="background: #ffd93d; color: #333;"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>Password</span>' : ''}
                ${seq.isPublic && seq.isDiscoverable ? '<span class="visibility-badge" style="background: #4caf50; color: white;"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>Discoverable</span>' : ''}
              </div>
            </div>
          </div>
          ${seq.description ? `<p class="sequence-description">${escapeHtml(seq.description)}</p>` : ''}
          <div class="sequence-meta">
            <span><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/></svg>${createdDate}</span>
            <span><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align: middle; margin-right: 4px;"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/></svg>${stageCount} stage${stageCount !== 1 ? 's' : ''}</span>
          </div>
          <div class="sequence-actions" onclick="event.stopPropagation()">
            <button class="btn btn-primary btn-small" onclick="viewSequence('${seq.id}')" data-tooltip="Open this sequence in the immersive VR viewer">
              View
            </button>
            <button class="btn btn-secondary btn-small" onclick="editSequence('${seq.id}')" data-tooltip="Edit stages, skyboxes, and content in the visual editor">
              Editor
            </button>
            <button class="btn btn-secondary btn-small" onclick="editSequenceSettings('${seq.id}')" data-tooltip="Change name, description, and privacy settings">
              Settings
            </button>
            <div style="width: 1px; background: #e0e0e0; margin: 0 8px; height: 24px;"></div>
            <button class="btn-icon" onclick="shareSequence('${seq.id}')" data-tooltip="Copy shareable link to clipboard">
              Link
            </button>
            <button class="btn-icon" onclick="showEmbedModal('${seq.id}')" data-tooltip="Get HTML embed code for your website">
              Embed
            </button>
            ${canExport ? `<button class="btn-icon" onclick="showExportModal('${seq.id}')" data-tooltip="Download as standalone package for self-hosting" style="color: #9c27b0;">
              Export
            </button>` : ''}
            <button class="btn-icon" onclick="toggleVisibility('${seq.id}')" data-tooltip="${seq.isPublic ? 'Click to make private (only you can view)' : 'Click to make public (anyone with link can view)'}" style="color: ${seq.isPublic ? '#4caf50' : '#999'};">
              ${seq.isPublic ? 'Public' : 'Private'}
            </button>
            ${seq.isPublic ? `<button class="btn-icon" onclick="toggleDiscoverable('${seq.id}')" data-tooltip="${seq.isDiscoverable ? 'Remove from public Discover page' : 'Show on public Discover page for everyone to find'}" style="color: ${seq.isDiscoverable ? '#4caf50' : '#999'};">
              ${seq.isDiscoverable ? 'Discoverable' : 'Hidden'}
            </button>` : ''}
            <button class="btn-icon" onclick="duplicateSeq('${seq.id}')" data-tooltip="Create a copy of this sequence">
              Copy
            </button>
            <button class="btn-icon" onclick="deleteSeq('${seq.id}')" data-tooltip="Permanently delete this sequence" style="color: #ff6b6b;">
              Delete
            </button>
          </div>
        </div>
      `;
    })
    .join('');

  // Add click handlers to cards
  document.querySelectorAll('.sequence-card').forEach((card) => {
    card.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.sequence-actions')) {
        const id = card.getAttribute('data-id');
        if (id) editSequence(id);
      }
    });
  });
}

/**
 * View sequence in viewer - uses the best available URL (slug if available)
 */
(window as any).viewSequence = function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (sequence) {
    // Use getShareableLink to get the preferred URL (slug if available)
    const shareUrl = getShareableLink(id, sequence, currentUserProfile?.username);
    // Extract just the path from the full URL
    const url = new URL(shareUrl);
    window.open(url.pathname, '_blank');
  }
};

/**
 * Edit sequence config (stages, skybox, etc.)
 */
/**
 * Edit sequence config (stages, skybox, etc.)
 */
function editSequence(id: string) {
  // Redirect to config editor
  window.location.href = `/dashboard/editor.html?id=${id}`;
}
(window as any).editSequence = editSequence;

/**
 * Edit sequence settings (name, description, analytics, etc.)
 */
(window as any).editSequenceSettings = function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (sequence) {
    openModal(sequence);
  }
};

/**
 * Share sequence
 */
(window as any).shareSequence = async function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (!sequence) return;

  // Pass sequence and username for slug-based URLs
  const shareUrl = getShareableLink(id, sequence, currentUserProfile?.username);

  try {
    await navigator.clipboard.writeText(shareUrl);
    showToast('Link copied to clipboard!', 'success');
  } catch (error) {
    prompt('Copy this link:', shareUrl);
  }
};

/**
 * Toggle sequence visibility
 */
(window as any).toggleVisibility = async function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (!sequence) return;

  try {
    const newVisibility = !sequence.isPublic;
    await toggleSequenceVisibility(id, newVisibility);

    // Update local state
    sequence.isPublic = newVisibility;
    // If making private, also turn off discoverable
    if (!newVisibility) {
      sequence.isDiscoverable = false;
    }
    renderSequences();

    showToast(`Sequence is now ${newVisibility ? 'public' : 'private'}`, 'success');
  } catch (error) {
    logger.error('Failed to toggle visibility:', error);
    showToast('Failed to update visibility', 'error');
  }
};

/**
 * Toggle sequence discoverability
 */
(window as any).toggleDiscoverable = async function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (!sequence) return;

  // Only allow toggling if tour is public
  if (!sequence.isPublic) {
    showToast('Tour must be public to be discoverable', 'error');
    return;
  }

  try {
    const newDiscoverable = !sequence.isDiscoverable;
    await updateSequence(id, { isDiscoverable: newDiscoverable });

    // Update local state
    sequence.isDiscoverable = newDiscoverable;
    renderSequences();

    showToast(
      newDiscoverable ? 'Now visible on discover page' : 'Hidden from discover page',
      'success'
    );
  } catch (error) {
    logger.error('Failed to toggle discoverability:', error);
    showToast('Failed to update discoverability', 'error');
  }
};

/**
 * Duplicate sequence
 */
(window as any).duplicateSeq = async function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (!sequence) return;

  try {
    const newId = await duplicateSequence(currentUser!.uid, id, `${sequence.name} (Copy)`);
    logger.info('Tour duplicated:', newId);

    // Reload tours
    await loadSequences();
    showToast('Tour duplicated successfully', 'success');
  } catch (error) {
    logger.error('Failed to duplicate tour:', error);
    showToast('Failed to duplicate tour', 'error');
  }
};

/**
 * Delete tour
 */
(window as any).deleteSeq = async function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (!sequence) return;

  const confirmed = await showConfirm(`Are you sure you want to delete "${sequence.name}"? This cannot be undone.`, 'Delete Tour', '🗑️');
  if (!confirmed) {
    return;
  }

  try {
    await deleteSequence(id);
    logger.info('Tour deleted');

    // Reload tours
    await loadSequences();
    showToast('Tour deleted successfully', 'success');
  } catch (error) {
    logger.error('Failed to delete tour:', error);
    showToast('Failed to delete tour', 'error');
  }
};

/**
 * Create new sequence
 */


/**
 * Open create/edit modal
 */
function openModal(sequence?: Sequence) {
  if (!sequenceModal || !modalTitle || !sequenceForm || !saveBtnText) return;

  editingSequenceId = sequence?.id || null;

  if (sequence) {
    modalTitle.textContent = 'Edit Tour';
    sequenceNameInput.value = sequence.name;
    sequenceDescInput.value = sequence.description || '';
    sequencePublicCheckbox.checked = sequence.isPublic;
    sequenceDiscoverableCheckbox.checked = sequence.isDiscoverable || false;
    sequenceAnalyticsCheckbox.checked = sequence.analyticsEnabled !== false; // Default to true
    saveBtnText.textContent = 'Save Changes';

    // Show/hide discoverable option based on public status
    if (sequence.isPublic) {
      discoverableGroup.style.display = 'block';
    } else {
      discoverableGroup.style.display = 'none';
    }

    // Show password status if editing
    if (sequence.password) {
      passwordStatus.style.display = 'block';
      passwordStatusText.textContent = 'Currently password protected';
      passwordStatusText.style.color = '#2e7d32';
      passwordHelpText.textContent = 'Enter a new password to change it, or leave empty to keep current password';
      removePasswordBtn.style.display = 'block';
    } else {
      passwordStatus.style.display = 'none';
      passwordHelpText.textContent = 'Viewers will need to enter this password to access the tour';
      removePasswordBtn.style.display = 'none';
    }
    sequencePasswordInput.value = '';
  } else {
    modalTitle.textContent = 'Create New Tour';
    sequenceForm.reset();
    sequenceAnalyticsCheckbox.checked = true; // Default to enabled for new tours
    saveBtnText.textContent = 'Create Tour';

    // Hide discoverable option by default for new tours
    discoverableGroup.style.display = 'none';

    // Hide password status for new tours
    passwordStatus.style.display = 'none';
    passwordHelpText.textContent = 'Viewers will need to enter this password to access the tour';
    removePasswordBtn.style.display = 'none';
  }

  sequenceModal.classList.add('show');
}

/**
 * Close modal
 */
function closeModal() {
  if (!sequenceModal || !sequenceForm) return;
  
  sequenceModal.classList.remove('show');
  sequenceForm.reset();
  editingSequenceId = null;
}



/**
 * Toggle discoverable option based on public checkbox
 */


/**
 * Submit form
 */


/**
 * Remove password protection
 */


/**
 * Logout
 */


/**
 * Escape HTML
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Workspace selector change handler
 */


/**
 * Show embed modal
 */
(window as any).showEmbedModal = function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (!sequence) return;

  // Generate initial embed code
  updateEmbedCode(id);

  // Show modal
  embedModal.classList.add('show');

  // Store current sequence ID for updates
  (embedModal as any).dataset.sequenceId = id;
};

/**
 * Update embed code based on options
 */
function updateEmbedCode(sequenceId: string) {
  // Add null checks for all elements
  if (!embedWidthInput || !embedHeightInput || !embedControlsCheckbox ||
      !embedBrandingCheckbox || !embedAutoplayCheckbox || !embedCodeTextarea) {
    logger.error('Embed modal elements not found');
    return;
  }

  const width = embedWidthInput.value || '100%';
  const height = embedHeightInput.value || '600px';
  const controls = embedControlsCheckbox.checked;
  const branding = embedBrandingCheckbox.checked;
  const autoplay = embedAutoplayCheckbox.checked;

  const code = getEmbedCode(sequenceId, {
    width,
    height,
    controls,
    branding,
    autoplay
  });

  embedCodeTextarea.value = code;
}

// Embed modal event listeners


/**
 * Show export modal (available to all tiers)
 */
(window as any).showExportModal = function (id: string) {
  const sequence = sequences.find((s) => s.id === id);
  if (!sequence) return;

  // Check permission - provide specific feedback
  if (!isFeatureEnabled('SELF_HOST_EXPORT')) {
    showToast('Export feature is currently disabled. Please try again later.', 'error');
    return;
  }
  if (!currentUserProfile) {
    showToast('Please sign in to export tours.', 'error');
    return;
  }
  if (!hasFeatureAccess(currentUserProfile, 'self_host_export')) {
    showToast('Your plan does not include tour exports. Please upgrade.', 'error');
    return;
  }

  if (!exportModal) {
    logger.error('Export modal not found');
    return;
  }

  // Reset checkboxes to defaults
  if (exportBrandingCheckbox) exportBrandingCheckbox.checked = false;
  if (exportControlsCheckbox) exportControlsCheckbox.checked = true;

  // Store current sequence ID
  (exportModal as any).dataset.sequenceId = id;

  // Show modal
  exportModal.classList.add('show');
};

/**
 * Download export package
 */
async function handleExportDownload() {
  if (!exportModal) return;

  const sequenceId = (exportModal as any).dataset.sequenceId;
  const sequence = sequences.find((s) => s.id === sequenceId);

  if (!sequence) {
    showToast('Tour not found', 'error');
    return;
  }

  // Get options
  const options: ExportOptions = {
    includeBranding: exportBrandingCheckbox?.checked ?? false,
    includeControls: exportControlsCheckbox?.checked ?? true,
    pageTitle: sequence.name
  };

  try {
    // Disable button and show loading
    if (downloadExportBtn) {
      downloadExportBtn.disabled = true;
      downloadExportBtn.textContent = 'Generating...';
    }

    // Generate and download
    await downloadTourPackage(sequence, options);

    showToast('Export package downloaded successfully!', 'success');

    // Close modal
    exportModal.classList.remove('show');

  } catch (error) {
    logger.error('Export failed:', error);
    showToast('Failed to generate export package', 'error');
  } finally {
    if (downloadExportBtn) {
      downloadExportBtn.disabled = false;
      downloadExportBtn.textContent = 'Download Package';
    }
  }
}

/**
 * Invite member modal handlers
 */


/**
 * Check for pending invitations on init
 */
async function checkPendingInvitations() {
  if (!currentUser?.email) return;

  try {
    const invitations = await getPendingInvitations(currentUser.email);

    if (invitations.length > 0) {
      logger.debug('Found pending invitations:', invitations.length);

      // Show invitations section
      const invitationsSection = document.getElementById('invitations-section');
      const invitationsList = document.getElementById('invitations-list');

      if (invitationsSection && invitationsList) {
        invitationsSection.style.display = 'block';

        // Render invitation cards
        invitationsList.innerHTML = invitations.map((invite) => {
          const createdDate = invite.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date';

          return `
            <div class="invitation-card" style="background: #f8f9ff; border: 2px solid #c62828; border-radius: 8px; padding: 16px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
              <div>
                <h3 style="font-size: 16px; font-weight: 600; color: #333; margin-bottom: 4px;">
                  Organization Invitation
                </h3>
                <p style="font-size: 14px; color: #666; margin-bottom: 4px;">
                  You've been invited to join an organization
                </p>
                <p style="font-size: 12px; color: #999;">
                  Invited ${createdDate}
                </p>
              </div>
              <div style="display: flex; gap: 8px;">
                <button class="btn btn-primary btn-small" onclick="acceptInvite('${invite.id}')" style="padding: 8px 16px; font-size: 14px;">
                  ✓ Accept
                </button>
                <button class="btn btn-secondary btn-small" onclick="declineInvite('${invite.id}')" style="padding: 8px 16px; font-size: 14px;">
                  ✗ Decline
                </button>
              </div>
            </div>
          `;
        }).join('');
      }
    }
  } catch (error) {
    logger.error('Error checking invitations:', error);
  }
}

/**
 * Accept an invitation
 */
(window as any).acceptInvite = async function(invitationId: string) {
  try {
    await acceptInvitation(invitationId);
    showToast('Invitation accepted! Reloading...', 'success');
    // Reload page to refresh organizations and sequences
    setTimeout(() => window.location.reload(), 1000);
  } catch (error) {
    logger.error('Error accepting invitation:', error);
    showToast('Failed to accept invitation', 'error');
  }
};

/**
 * Decline an invitation
 */
(window as any).declineInvite = async function(invitationId: string) {
  const confirmed = await showConfirm('Are you sure you want to decline this invitation?', 'Decline Invitation', '✉️');
  if (!confirmed) {
    return;
  }

  try {
    await declineInvitation(invitationId);
    showToast('Invitation declined', 'info');
    // Reload page to refresh invitations
    setTimeout(() => window.location.reload(), 1000);
  } catch (error) {
    logger.error('Error declining invitation:', error);
    showToast('Failed to decline invitation', 'error');
  }
};

/**
 * Open user settings modal
 */
async function openUserSettings() {
  if (!currentUser) return;

  try {
    // Get or create user profile
    const userProfile = await ensureUserProfile(currentUser.uid, currentUser.email, currentUser.displayName);

    // Set checkbox state
    if (userAnalyticsCheckbox) {
      userAnalyticsCheckbox.checked = userProfile.analyticsEnabled !== false;
    }

    userSettingsModal.classList.add('show');
  } catch (error) {
    logger.error('Error loading user settings:', error);
    showToast('Failed to load settings', 'error');
  }
}

/**
 * Close user settings modal
 */
function closeUserSettings() {
  userSettingsModal.classList.remove('show');
  userSettingsForm.reset();
}

/**
 * Save user settings
 */
async function saveUserSettings(e: Event) {
  e.preventDefault();

  if (!currentUser || !userAnalyticsCheckbox) return;

  try {
    const analyticsEnabled = userAnalyticsCheckbox.checked;

    // Update user profile
    await toggleUserAnalytics(currentUser.uid, analyticsEnabled);

    showToast(`Analytics ${analyticsEnabled ? 'enabled' : 'disabled'} for your account.`, 'success');
    closeUserSettings();
  } catch (error) {
    logger.error('Error saving user settings:', error);
    showToast('Failed to save settings', 'error');
  }
}

/**
 * Save sequence (create or update)
 */
async function saveSequence(e: Event) {
  e.preventDefault();

  if (!currentUser || !sequenceNameInput) return;

  const name = sequenceNameInput.value.trim();
  const description = sequenceDescInput.value.trim();
  const isPublic = sequencePublicCheckbox.checked;
  const isDiscoverable = sequenceDiscoverableCheckbox.checked;
  const analyticsEnabled = sequenceAnalyticsCheckbox.checked;
  const password = sequencePasswordInput.value.trim() || null;

  if (!name) {
    showToast('Please enter a sequence name', 'error');
    return;
  }

  try {
    saveBtn.disabled = true;
    saveBtnText.textContent = 'Saving...';

    if (editingSequenceId) {
      // Update existing
      const updates: Partial<Sequence> = {
        name,
        description,
        isPublic,
        isDiscoverable: isPublic && isDiscoverable,
        analyticsEnabled
      };

      if (password) {
        updates.password = await bcrypt.hash(password, 10);
      }

      await updateSequence(editingSequenceId, updates);
      showToast('Tour updated successfully', 'success');
    } else {
      // Create new
      const orgId = currentWorkspace === 'personal' ? null : currentWorkspace;
      
      const defaultConfig: ExperienceConfig = {
        experience: {
          title: name,
          description: description || ''
        },
        stages: []
      };

      const newTourId = await createSequence(
        currentUser.uid,
        name,
        defaultConfig,
        isPublic,
        description,
        orgId,
        password,
        analyticsEnabled,
        isPublic && isDiscoverable
      );

      // Redirect directly to the editor - don't make users hunt for their new tour!
      window.location.href = `/dashboard/editor.html?id=${newTourId}`;
      return;
    }

    closeModal();
    await loadSequences();

  } catch (error) {
    logger.error('Failed to save tour:', error);
    showToast('Failed to save tour', 'error');
  } finally {
    saveBtn.disabled = false;
    saveBtnText.textContent = editingSequenceId ? 'Save Changes' : 'Create Tour';
  }
}


// Initialize on auth state change
onAuthChange((user) => {
  if (user) {
    // Ensure DOM is ready before init
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initializeDOM();
        setupEventListeners();
        init().then(() => {
          checkPendingInvitations();
        });
      });
    } else {
      initializeDOM();
      setupEventListeners();
      init().then(() => {
        checkPendingInvitations();
      });
    }
  } else {
    window.location.href = '/dashboard/auth.html';
  }
});

function setupEventListeners() {
  // Hide pricing navigation buttons if feature is disabled
  if (!isFeatureEnabled('PRICING_PAGE')) {
    document.querySelectorAll('.pricing-nav-btn').forEach((btn) => {
      (btn as HTMLElement).style.display = 'none';
    });
  }

  // Mobile Menu Event Listeners
  const mobileCreateOrgBtn = document.getElementById('mobile-create-org-btn');
  const mobileSettingsBtn = document.getElementById('mobile-settings-btn');
  const mobileLogoutBtn = document.getElementById('mobile-logout-btn');

  if (mobileCreateOrgBtn) {
    mobileCreateOrgBtn.addEventListener('click', async () => {
      // Check if user has Pro access
      const hasPro = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');
      
      if (!hasPro && currentUser) {
        await showUpgradeModal(currentUser.uid, currentUser.email);
        return;
      }
      
      // Close mobile menu
      const mobileMenu = document.getElementById('mobile-menu');
      const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
      if (mobileMenu) mobileMenu.classList.remove('active');
      if (mobileMenuOverlay) mobileMenuOverlay.classList.remove('active');
      document.body.style.overflow = '';

      if (orgModal) orgModal.classList.add('show');
    });
  }

  if (mobileSettingsBtn) {
    mobileSettingsBtn.addEventListener('click', () => {
      // Close mobile menu
      const mobileMenu = document.getElementById('mobile-menu');
      const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
      if (mobileMenu) mobileMenu.classList.remove('active');
      if (mobileMenuOverlay) mobileMenuOverlay.classList.remove('active');
      document.body.style.overflow = '';

      openUserSettings();
    });
  }

  if (mobileLogoutBtn) {
    mobileLogoutBtn.addEventListener('click', async () => {
      try {
        await logOut();
        window.location.href = '/dashboard/auth.html';
      } catch (error) {
        logger.error('Logout error:', error);
        await showAlert('Failed to sign out', 'Error', '❌');
      }
    });
  }

  // User settings event listeners - use shared settings modal
  if (userSettingsBtn) {
    userSettingsBtn.addEventListener('click', async () => {
      if (currentUserProfile && currentUser) {
        await showSettingsModal(currentUserProfile, currentUser.uid);
      } else {
        await showAlert('Profile not loaded yet. Please wait.', 'Not Ready', '⏳');
      }
    });
  }
  if (cancelUserSettingsBtn) {
    cancelUserSettingsBtn.addEventListener('click', closeUserSettings);
  }
  if (userSettingsForm) {
    userSettingsForm.addEventListener('submit', saveUserSettings);
  }

  // Close modal when clicking outside
  if (userSettingsModal) {
    userSettingsModal.addEventListener('click', (e) => {
      if (e.target === userSettingsModal) closeUserSettings();
    });
  }

  // Embed modal event listeners
  if (closeEmbedBtn) {
    closeEmbedBtn.addEventListener('click', () => {
      embedModal.classList.remove('show');
    });
  }

  if (embedModal) {
    embedModal.addEventListener('click', (e) => {
      if (e.target === embedModal) {
        embedModal.classList.remove('show');
      }
    });
  }

  // Update embed code when options change
  [embedWidthInput, embedHeightInput, embedControlsCheckbox, embedBrandingCheckbox, embedAutoplayCheckbox].forEach((element) => {
    if (element) {
      element.addEventListener('change', () => {
        const sequenceId = (embedModal as any).dataset.sequenceId;
        if (sequenceId) {
          updateEmbedCode(sequenceId);
        }
      });
    }
  });

  // Copy embed code
  if (copyEmbedBtn) {
    copyEmbedBtn.addEventListener('click', async () => {
      try {
        if (!embedCodeTextarea) return;

        await navigator.clipboard.writeText(embedCodeTextarea.value);
        const originalText = copyEmbedBtn.textContent;
        copyEmbedBtn.textContent = '✅ Copied!';
        setTimeout(() => {
          copyEmbedBtn.textContent = originalText;
        }, 2000);
        showToast('Embed code copied to clipboard!', 'success');
      } catch (error) {
        showToast('Failed to copy to clipboard', 'error');
      }
    });
  }

  // Export modal event listeners
  if (closeExportBtn) {
    closeExportBtn.addEventListener('click', () => {
      if (exportModal) exportModal.classList.remove('show');
    });
  }

  if (exportModal) {
    exportModal.addEventListener('click', (e) => {
      if (e.target === exportModal) {
        exportModal.classList.remove('show');
      }
    });
  }

  if (downloadExportBtn) {
    downloadExportBtn.addEventListener('click', handleExportDownload);
  }

  // Invite Member
  if (inviteMemberBtn) {
    inviteMemberBtn.addEventListener('click', () => {
      if (inviteModal) inviteModal.classList.add('show');
    });
  }

  if (cancelInviteBtn) {
    cancelInviteBtn.addEventListener('click', () => {
      if (inviteModal) inviteModal.classList.remove('show');
      if (inviteForm) inviteForm.reset();
    });
  }

  if (inviteModal) {
    inviteModal.addEventListener('click', (e) => {
      if (e.target === inviteModal) {
        inviteModal!.classList.remove('show');
        if (inviteForm) inviteForm.reset();
      }
    });
  }

  // Create Org
  if (createOrgBtn) {
    createOrgBtn.addEventListener('click', async () => {
      // Check if user has Pro access
      const hasPro = currentUserProfile && hasFeatureAccess(currentUserProfile, 'uploads');

      if (!hasPro && currentUser) {
        await showUpgradeModal(currentUser.uid, currentUser.email);
        return;
      }

      if (orgModal) orgModal.classList.add('show');
    });
  }

  if (cancelOrgBtn) {
    cancelOrgBtn.addEventListener('click', () => {
      if (orgModal) orgModal.classList.remove('show');
      if (orgForm) orgForm.reset();
    });
  }

  if (orgModal) {
    orgModal.addEventListener('click', (e) => {
      if (e.target === orgModal) {
        orgModal.classList.remove('show');
        if (orgForm) orgForm.reset();
      }
    });
  }

  // Create Sequence
  if (createSequenceBtn) {
    createSequenceBtn.addEventListener('click', () => {
      openModal();
    });
  }

  if (emptyState) {
    emptyState.querySelector('.btn')?.addEventListener('click', () => {
      openModal();
    });
  }

  if (cancelBtn) {
    cancelBtn.addEventListener('click', closeModal);
  }

  if (sequenceModal) {
    sequenceModal.addEventListener('click', (e) => {
      if (e.target === sequenceModal) closeModal();
    });
  }

  if (sequenceForm) {
    sequenceForm.addEventListener('submit', saveSequence);
  }

  if (sequencePublicCheckbox) {
    sequencePublicCheckbox.addEventListener('change', () => {
      if (sequencePublicCheckbox.checked) {
        discoverableGroup.style.display = 'block';
      } else {
        discoverableGroup.style.display = 'none';
        sequenceDiscoverableCheckbox.checked = false; // Uncheck when hiding
      }
    });
  }

  if (removePasswordBtn) {
    removePasswordBtn.addEventListener('click', async () => {
      if (!editingSequenceId) return;

      const confirmed = await showConfirm('Remove password protection from this sequence?', 'Remove Password', '🔓');
      if (!confirmed) {
        return;
      }

      try {
        removePasswordBtn.disabled = true;
        removePasswordBtn.textContent = 'Removing...';

        // Remove password from sequence
        await updateSequence(editingSequenceId, { password: null });

        // Hide password status
        if (passwordStatus) passwordStatus.style.display = 'none';
        if (removePasswordBtn) removePasswordBtn.style.display = 'none';
        if (passwordHelpText) passwordHelpText.textContent = 'Viewers will need to enter this password to access the sequence';
        if (sequencePasswordInput) sequencePasswordInput.value = '';

        // Reload sequences to update UI
        await loadSequences();

        showToast('Password protection removed', 'success');
      } catch (error) {
        logger.error('Failed to remove password:', error);
        showAlert('Failed to remove password. Please try again.', 'Error', '❌');
      } finally {
        if (removePasswordBtn) {
          removePasswordBtn.disabled = false;
          removePasswordBtn.textContent = 'Remove Password Protection';
        }
      }
    });
  }

  if (logoutBtn) {
    logoutBtn.addEventListener('click', async () => {
      try {
        await logOut();
        window.location.href = '/dashboard/auth.html';
      } catch (error) {
        logger.error('Logout error:', error);
        await showAlert('Failed to sign out', 'Error', '❌');
      }
    });
  }

  // Usage button handler
  const usageBtn = document.getElementById('user-usage-btn');
  if (usageBtn) {
    usageBtn.addEventListener('click', async () => {
      if (currentUserProfile) {
        await showUsageModal(currentUserProfile);
      } else {
        await showAlert('Profile not loaded yet. Please wait.', 'Not Ready', '⏳');
      }
    });
  }

  // Note: Settings button handler is already set up above (line ~1210) to call openUserSettings()

  if (upgradeBtn) {
    // Always attach click handler - showUpgradeModal handles feature flag internally
    // (shows Pac-Man game when pricing page is disabled, or real pricing modal when enabled)
    upgradeBtn.addEventListener('click', async () => {
      if (currentUser) {
        await showUpgradeModal(currentUser.uid, currentUser.email);
      } else {
        window.location.href = '/dashboard/auth.html';
      }
    });
  }

  // User dropdown toggle - using shared function
  setupDropdownToggle();

  if (workspaceSelector) {
    workspaceSelector.addEventListener('change', async () => {
      currentWorkspace = workspaceSelector.value;
      logger.debug('Switched to workspace:', currentWorkspace);

      // Show/hide invite button based on workspace
      if (inviteMemberBtn) {
        if (currentWorkspace === 'personal') {
          inviteMemberBtn.style.display = 'none';
        } else {
          inviteMemberBtn.style.display = 'inline-block';
        }
      }

      // Update Pro badge visibility
      updateProBadge();

      await loadSequences();
    });
  }
}
